/**
 * Denetim Kaydı — eski sistemde KARŞILIĞI YOKTU.
 *
 * Eski Balder'da kim neyi ne zaman değiştirdi bilgisi hiçbir yerde tutulmuyordu;
 * `post.php` her durumda 200 + "başarıyla güncellendi" dönüyor, hatalı işlem bile
 * iz bırakmıyordu. `auditLogs` append-only'dur: istemci yazamaz, hiç silinemez.
 *
 * Yetki: YALNIZCA `boss` (Rules: `allow get, list: if isBoss()`).
 *
 * Sorgu notu: filtreler İSTEMCİ tarafında uygulanır. Sunucu tarafında yalnızca
 * `orderBy(at desc) + limit` vardır; böylece her filtre kombinasyonu için ayrı
 * bileşik indeks gerekmez.
 */

import { useMemo, useState } from "react";
import { orderBy, limit } from "firebase/firestore";
import { PageHeader, Spinner, EmptyState, StatusBadge, useToast } from "../components";
import { useCollection, useCallableAction } from "../hooks";
import { useCan } from "../lib/auth";
import { dt, n } from "../lib/format";
import { NoAccess } from "../components";
import type { ReportOutput } from "./Reports";
import { ErrorNote, Field } from "../components";

/**
 * ★ ALAN ADLARI SUNUCUYLA HİZALANDI.
 *
 * TESPİT: bu arayüz, sunucunun HİÇ YAZMADIĞI dört alanı okuyordu —
 *   `userName`, `errorCode`, `errorMessage`, `detail`
 * ve sunucunun GERÇEKTEN yazdığı dört alanı hiç okumuyordu —
 *   `before`, `after`, `error`, `reason`
 * (yazan taraf: `functions/src/core/logging.ts:29-53`).
 *
 * Sonuçları canlı veriyle ölçüldü (83 kayıtlık örneklem):
 *   · "Ayrıntı" düğmesi `r.detail` koşuluna bağlıydı; bu alan hiçbir kayıtta
 *     olmadığı için düğme HİÇ ÇIKMIYOR, `DetailPanel` ise ulaşılamaz ölü kod
 *     hâline gelmişti. 81 kayıttaki `after` ve 60 kayıttaki `before` gövdesi
 *     ekranda hiçbir şekilde görünmüyordu.
 *   · "Kullanıcı" sütunu her satırda ham UID gösteriyordu (`userName` yok).
 *   · Hata mesajı sütunu hep boştu (`errorMessage` yerine alan adı `error`).
 * Yani denetim kaydı ekranı "ne değişti"yi HİÇBİR ZAMAN gösteremiyordu.
 */
export interface AuditEntry {
  id: string;
  /** Örn. "order.status.change", "component.update", "user.role.change" */
  action: string;
  entity: string;
  entityId: string;
  userId: string;
  result: "ok" | "error" | string;
  /** Sunucu alan adı `error` (eskiden yanlışlıkla `errorMessage` okunuyordu). */
  error?: string | null;
  /** İşlemin gerekçesi — 7 çağrı yerinde doldurulur. */
  reason?: string | null;
  at?: { toDate: () => Date } | null;
  /** Değişiklik öncesi gövde. PII içermez — sunucuda `redact()` uygulanır. */
  before?: unknown;
  /** Değişiklik sonrası gövde. PII içermez — sunucuda `redact()` uygulanır. */
  after?: unknown;
}

/** `userProfiles` dokümanı — UID'yi okunabilir ada çevirmek için. */
interface UserProfileDoc {
  id: string;
  fullName?: string | null;
  name?: string | null;
  surname?: string | null;
}

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

const PAGE_STEP = 200;

export default function AuditLog() {
  const can = useCan();

  if (!can.isBoss) {
    return (
      <PageHeader title="Denetim Kaydı">
        <NoAccess what="Denetim kayıtları" />
      </PageHeader>
    );
  }

  return (
    <PageHeader title="Denetim Kaydı">
      <AuditTable />
    </PageHeader>
  );
}

function AuditTable() {
  const toast = useToast();
  const [max, setMax] = useState(PAGE_STEP);

  const { data, loading, error } = useCollection<AuditEntry>("auditLogs", [
    orderBy("at", "desc"),
    limit(max),
  ]);

  const exportLog = useCallableAction<{ from: string; to: string }, ReportOutput>(
    "exportAuditLog",
  );

  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [userId, setUserId] = useState("");
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState<AuditEntry | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exportError, setExportError] = useState<string | null>(null);

  /**
   * Kullanıcı adı denetim kaydında TUTULMAZ (sunucu yalnızca `userId` yazar).
   * Ad çözümü `userProfiles` üzerinden yapılır — bu koleksiyon tam da bunun
   * için var ("kim değiştirdi" gösterimleri) ve `isActive()` ile okunabilir.
   * Profil bulunamazsa ham UID gösterilir; sessizce boş bırakılmaz.
   */
  const profiles = useCollection<UserProfileDoc>("userProfiles");
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profiles.data) {
      const full = p.fullName ?? [p.name, p.surname].filter(Boolean).join(" ").trim();
      if (full) m.set(p.id, full);
    }
    return (uid: string) => m.get(uid) ?? uid;
  }, [profiles.data]);

  const actions = useMemo(
    () => Array.from(new Set(data.map((d) => d.action))).sort((a, b) => a.localeCompare(b, "tr")),
    [data],
  );
  const entities = useMemo(
    () => Array.from(new Set(data.map((d) => d.entity))).sort((a, b) => a.localeCompare(b, "tr")),
    [data],
  );
  const users = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of data) m.set(d.userId, nameOf(d.userId));
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], "tr"));
  }, [data, nameOf]);

  const rows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    return data.filter((d) => {
      if (action && d.action !== action) return false;
      if (entity && d.entity !== entity) return false;
      if (userId && d.userId !== userId) return false;
      if (onlyErrors && d.result === "ok") return false;
      if (!needle) return true;
      return (
        d.entityId.toLocaleLowerCase("tr").includes(needle) ||
        d.action.toLocaleLowerCase("tr").includes(needle) ||
        nameOf(d.userId).toLocaleLowerCase("tr").includes(needle) ||
        (d.error ?? "").toLocaleLowerCase("tr").includes(needle) ||
        (d.reason ?? "").toLocaleLowerCase("tr").includes(needle)
      );
    });
  }, [data, action, entity, userId, onlyErrors, q, nameOf]);

  const errorCount = data.filter((d) => d.result !== "ok").length;

  async function runExport() {
    setExportError(null);
    if (!from || !to) {
      setExportError("Dışa aktarma için başlangıç ve bitiş tarihi seçilmelidir.");
      return;
    }
    if (from > to) {
      setExportError("Başlangıç tarihi bitiş tarihinden sonra olamaz.");
      return;
    }
    const res = await exportLog.run({ from, to });
    if (res) {
      toast.success(
        `${n(res.rowCount)} kayıt dışa aktarıldı. Bağlantı 15 dakika geçerlidir.`,
      );
      window.open(res.url, "_blank", "noopener,noreferrer");
    }
  }

  return (
    <>
      <p className="mb-4 max-w-3xl text-sm text-slate-500">
        Yan etkili her işlem buraya yazılır ve <strong>hiç silinemez</strong>. Kayıtlar kişisel veri
        içermez; hassas alanlar sunucuda maskelenir.
      </p>

      {errorCount > 0 ? (
        <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          Son {n(data.length)} kayıtta {n(errorCount)} başarısız işlem var.
          <button type="button" className="ml-2 underline" onClick={() => setOnlyErrors(true)}>
            Listele
          </button>
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-1 gap-2 rounded border border-slate-200 bg-slate-50 p-3 md:grid-cols-5">
        <Field label="İşlem">
          <select className={INPUT} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">Tümü</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Kayıt Türü">
          <select className={INPUT} value={entity} onChange={(e) => setEntity(e.target.value)}>
            <option value="">Tümü</option>
            {entities.map((e2) => (
              <option key={e2} value={e2}>
                {e2}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Kullanıcı">
          <select className={INPUT} value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Tümü</option>
            {users.map(([uid, label]) => (
              <option key={uid} value={uid}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Ara">
          <input
            className={INPUT}
            value={q}
            placeholder="Kayıt no, işlem, kullanıcı"
            onChange={(e) => setQ(e.target.value)}
          />
        </Field>
        <label className="flex items-end gap-2 pb-1.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={onlyErrors}
            onChange={(e) => setOnlyErrors(e.target.checked)}
          />
          Yalnızca hatalar
        </label>
      </div>

      {/* ── Dışa aktarma: 15 dk imzalı URL ───────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-end gap-2 rounded border border-slate-200 p-3">
        <Field label="Başlangıç">
          <input type="date" className={INPUT} value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Bitiş">
          <input type="date" className={INPUT} value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <button
          type="button"
          onClick={runExport}
          disabled={exportLog.loading}
          className="min-h-[44px] rounded border border-slate-300 px-4 py-1.5 text-sm hover:bg-slate-100 disabled:opacity-50 lg:min-h-0"
        >
          {exportLog.loading ? "Hazırlanıyor…" : "Excel'e Aktar"}
        </button>
        <div className="w-full">
          <ErrorNote error={exportError ?? exportLog.error} />
        </div>
      </div>

      <ErrorNote error={error} />
      {loading ? <Spinner /> : null}

      {!loading && rows.length === 0 ? (
        <EmptyState title="Kayıt bulunamadı" description="Filtreleri gözden geçirin." />
      ) : null}

      {rows.length > 0 ? (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
              <tr>
                {/* ★ TABLET/TELEFON: 6 sütun 1008px yer istiyordu, tablet dikeyde
                    (720px kap) bile yatay kayıyordu. İkincil sütunlar dar ekranda
                    gizlenir, içerikleri "İşlem" hücresinin altına taşınır. */}
                <th className="px-3 py-2">Zaman</th>
                <th className="hidden px-3 py-2 lg:table-cell">Kullanıcı</th>
                <th className="px-3 py-2">İşlem</th>
                <th className="hidden px-3 py-2 md:table-cell">Kayıt</th>
                <th className="px-3 py-2">Sonuç</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{dt(r.at ?? null)}</td>
                  <td className="hidden px-3 py-2 text-slate-700 lg:table-cell">{nameOf(r.userId)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">
                    {r.action}
                    {/* Dar ekranda gizlenen sütunların içeriği burada görünür. */}
                    <span className="mt-1 block font-sans text-xs text-slate-600 lg:hidden">
                      {nameOf(r.userId)}
                    </span>
                    <span className="mt-0.5 block font-sans text-xs text-slate-600 md:hidden">
                      {r.entity} <span className="font-mono">{r.entityId}</span>
                    </span>
                  </td>
                  <td className="hidden px-3 py-2 text-slate-600 md:table-cell">
                    <span className="text-xs text-slate-600">{r.entity}</span>{" "}
                    <span className="font-mono text-xs">{r.entityId}</span>
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge
                      label={r.result === "ok" ? "Başarılı" : "Hata"}
                      color={r.result === "ok" ? "success" : "danger"}
                    />
                    {/* Hata mesajı aynen gösterilir; sessiz başarı yok. */}
                    {r.error ? (
                      <span className="ml-2 text-xs text-red-700">{r.error}</span>
                    ) : null}
                    {r.reason ? (
                      <span className="ml-2 text-xs text-slate-500">{r.reason}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {/*
                      Koşul `detail` → `before`/`after`. Eski koşul hiçbir kayıtta
                      sağlanmadığı için düğme HİÇ görünmüyordu.
                    */}
                    {r.before !== undefined || r.after !== undefined ? (
                      <button
                        type="button"
                        /* Parmakla basılabilsin: dar ekranda 44px, masaüstünde eski ölçü. */
                        className="inline-flex min-h-[44px] items-center justify-center rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 lg:min-h-0 lg:px-2"
                        onClick={() => setDetail(r)}
                      >
                        Ayrıntı
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {data.length >= max ? (
        <button
          type="button"
          onClick={() => setMax((m) => m + PAGE_STEP)}
          className="mt-3 rounded border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100"
        >
          Daha eski kayıtları yükle
        </button>
      ) : null}

      {detail ? (
        <DetailPanel entry={detail} userName={nameOf(detail.userId)} onClose={() => setDetail(null)} />
      ) : null}
    </>
  );
}

/** JSON gövdesini okunabilir metne çevirir; `undefined` sessizce boş kalmaz. */
function bodyText(v: unknown): string {
  if (v === undefined) return "(bu işlemde kaydedilmedi)";
  if (v === null) return "null";
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function DetailPanel({
  entry,
  userName,
  onClose,
}: {
  entry: AuditEntry;
  userName: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/30" onClick={onClose}>
      <aside
        className="h-full w-full max-w-md overflow-y-auto bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-start justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold text-slate-800">{entry.action}</h3>
            <p className="text-xs text-slate-500">
              {entry.entity} · {entry.entityId}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm text-slate-500 hover:text-slate-800">
            Kapat
          </button>
        </header>

        <dl className="mb-4 space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Zaman</dt>
            <dd className="text-slate-800">{dt(entry.at ?? null)}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Kullanıcı</dt>
            <dd className="text-slate-800">{userName}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">Sonuç</dt>
            <dd className="text-slate-800">{entry.result}</dd>
          </div>
          {entry.reason ? (
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Gerekçe</dt>
              <dd className="text-slate-800">{entry.reason}</dd>
            </div>
          ) : null}
          {entry.error ? (
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Hata</dt>
              <dd className="font-mono text-red-700">{entry.error}</dd>
            </div>
          ) : null}
        </dl>

        {/*
          ★ ÖNCE / SONRA yan yana.
          `before` alanı 28 sunucu çağrı yerinin 21'inde HİÇ gönderilmiyor
          (bkz. rapor). O yüzden "kaydedilmedi" ile "boş" ayrımı açıkça yazılır:
          eskiden ikisi de aynı görünüyordu — daha doğrusu hiçbiri görünmüyordu.
        */}
        <section className="space-y-3">
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Önce
            </h4>
            <pre className="overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              {bodyText(entry.before)}
            </pre>
          </div>
          <div>
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sonra
            </h4>
            <pre className="overflow-x-auto rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              {bodyText(entry.after)}
            </pre>
          </div>
        </section>
      </aside>
    </div>
  );
}
